import { on } from '@ember/modifier';
import { and, not } from 'tio-ui/utilities';
import { Section } from 'tio-reimagined/components/layout';
import { t } from 'ember-intl';
import Button from 'tio-reimagined/components/button';
import Component from '@glimmer/component';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import CategorizedUpload from 'tio-common/components/tuition-assistance/forms/categorized-upload';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import type TASProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type TasProgramTemplateModel from 'tio-data/models/tas-program-template';

interface StepAttachmentsSignature {
  Args: {
    stepNumber: number;
    programInstance: TASProgramInstanceModel;
    programTemplate: TasProgramTemplateModel;
    attachmentClassificationString: string;
    attachmentsListInstructions: string;
    attachmentsListItems: string[];
    isAttachmentRequired: boolean;
    hasRequiredAttachments: boolean;
    onNext: () => void;
    hasAttempted: boolean;
    isComplete: boolean;
  };
}

export default class StepAttachments extends Component<StepAttachmentsSignature> {
  get displayNumber() {
    return this.args.stepNumber + 1;
  }

  get useCategorizedUpload() {
    const entries = this.args.programTemplate.documentEntriesFor(
      this.args.attachmentClassificationString
    );
    return entries.length > 0;
  }

  <template>
    <Section>
      <:header>
        <div class="tio-row-tight">
          <span
            class="tio-inline-row-center w-7 h-7 rounded-full tio-bg-brand tio-fg-inverse text-xs font-bold"
          >{{this.displayNumber}}</span>
          <span>
            {{t "tas.guided.steps.include_attachments"}}
            {{#if @isAttachmentRequired}}
              <span class="text-error-400">*</span>
            {{/if}}
          </span>
        </div>
      </:header>
      <:body>
        {{#if (and @hasAttempted @isAttachmentRequired (not @hasRequiredAttachments))}}
          <AlertBanner @intent="warning" class="mb-4">
            <p class="text-sm font-medium">{{t "tas.guided.steps.attachments_required"}}</p>
          </AlertBanner>
        {{/if}}

        {{#if this.useCategorizedUpload}}
          <CategorizedUpload
            @programTemplate={{@programTemplate}}
            @classification={{@attachmentClassificationString}}
            @attachable={{@programInstance}}
            @attachableType="tasProgramInstance"
            @existingAssets={{@programInstance.tasAssets}}
          />
        {{else}}
          <UploadAttachments
            @programTemplate={{@programTemplate}}
            @classification={{@attachmentClassificationString}}
            @attachable={{@programInstance}}
            @attachableType="tasProgramInstance"
            @useDropzone={{true}}
            @instructions={{@attachmentsListInstructions}}
            @items={{@attachmentsListItems}}
            @hideDiscarded={{true}}
          />
        {{/if}}

        <div class="flex pt-8">
          <Button @intent="primary" data-test-next-step {{on "click" @onNext}}>
            {{t "common.next"}}
          </Button>
        </div>
      </:body>
    </Section>
  </template>
}
